import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback, useMemo } from 'react';
import { useIntl } from 'umi';
import { SelectMultiple, Radio } from 'tea-component';
import TipContext from '@home/components/TipContext';
import useIntlExtend from '@intelligence/hooks/useIntlExtend';
import { buildSelectFilterAccepter } from '@intelligence/utils/commonUtils';
import { LANGUAGE_CN, LANGUAGE_EN } from '@home/_config/config';

import type { ISteamTagsSelectorProps } from './types';

const defaultSelectorConfig = {
  placeholder: 'dbi_top_steam_tags_placeholder',
  searchPlaceholder: 'dbi_top_steam_tags_search_placeholder',
  label: 'dbi_top_steam_tags',
  tipLabel: 'dbi_top_steam_tags',
  tipText: 'dbi_top_steam_tags_tip',
};

const SteamTagsSelector = (props: ISteamTagsSelectorProps, ref: React.Ref<any>): React.ReactElement => {
  const {
    onConfirm,
    value,
    AWidth,
    options,
    style,
    selectorConfig = defaultSelectorConfig,
    filterLogic: filterLogicProp = 'AND',
    onLogicChange,
    showLogicSelector = true,
    default: defaultValue,
  } = props;
  const { formatMessage } = useIntlExtend();
  const { locale } = useIntl();

  const { placeholder, searchPlaceholder, label, tipLabel, tipText, tipTextParams } = selectorConfig;

  // 搜索关键字
  const [keywords, setKeywords] = useState('');

  // 已选择 Tag 列表数据(不区分非搜索模式、搜索模式)
  const [selectedTags, setSelectedTags] = useState<string[] | undefined>(value || []);
  const [updateValue, setUpdateValue] = useState(false);

  // Filter logic state (AND/OR)
  const [filterLogic, setFilterLogic] = useState<'AND' | 'OR'>(filterLogicProp);

  // 组件内部 updateValue 修改时,更新值直接同步。外部处理时无值默认全选
  useEffect(() => {
    // 去除默认空时全选
    // if (updateValue) {
    //   setSelectedTags(value);
    // } else {
    //   setSelectedTags(value?.length ? value : allTagsValue);
    // }
    setSelectedTags(value);
  }, [value, updateValue]);

  // Sync filter logic from props
  useEffect(() => {
    setFilterLogic(filterLogicProp);
  }, [filterLogicProp]);

  // Highlight search keywords in text
  const highlightText = (text: string, search: string) => {
    if (!search) return text;
    const escapedSearch = search.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
    const regex = new RegExp(`(${escapedSearch})`, 'gi'); // Case-insensitive regex
    const parts = text.split(regex);
    return (
      <>
        {parts.map((part, index) =>
          regex.test(part) ? (
            <span key={index} style={{ color: "#326ff0" }}>{part}</span>
          ) : (
            part
          )
        )}
      </>
    );
  };

  const tagOptionsFilterAcceptor = useMemo(() => {
    return buildSelectFilterAccepter({
      separate: false,
      getOptionText: (option) => {
        // Use value as fallback since text might be a React element after highlighting
        const getStringValue = (val: any): string => {
          if (typeof val === 'string') return val;
          if (val === null || val === undefined) return '';
          return String(val?.value || '');
        };
        const defaultOptionText = getStringValue(option?.text) || getStringValue(option?.value) || '';
        switch (locale) {
          case LANGUAGE_CN: {
            // @ts-ignore
            const textCN = option?.textCN;
            return typeof textCN === 'string' ? textCN : defaultOptionText;
          }
          case LANGUAGE_EN: {
            // @ts-ignore
            const textEN = option?.textEN;
            return typeof textEN === 'string' ? textEN : defaultOptionText;
          }
          default:
            return defaultOptionText;
        }
      },
    });
  }, [locale]);

  // 当前展示的 options 选项列表（搜索列表或全部数据）
  const currentTagOptions = useMemo(() => {
    if (!keywords) {
      return options;
    }
    const searchKey = keywords?.toLowerCase();
    const filteredOptions = options?.filter((option: any) => {
      return tagOptionsFilterAcceptor(searchKey, option);
    });
    // Apply highlighting to filtered options
    return filteredOptions?.map((option: any) => ({
      ...option,
      text: highlightText(typeof option.text === 'string' ? option.text : '', keywords),
    }));
  }, [locale, options, keywords]);

  const buttonLabel = useMemo(() => {
    const selectedTagsCount = selectedTags?.length || 0;
    const tagsCount = options?.length || 0;
    let result: React.ReactNode = '';
    // !selectedTagsCount ||
    if (selectedTagsCount > 1 && selectedTagsCount === tagsCount) {
      // 没选择 Tag 或者 全选 Tag (包含 非搜索状态 和 搜索状态)
      result = formatMessage({ id: 'dbi_all' });
    } else if (selectedTagsCount < 2) {
      // 选择 Tag 数量小于2个
      const currentTag = selectedTags?.[0];
      const text = options?.find((item: any) => item.value === currentTag)?.text;
      result = text || currentTag;
    } else {
      // 选择 Tag 数量 大于等于 2 且 小于 全部 Tag(区分 非搜索状态 和 搜索状态)
      result = String(selectedTagsCount);
    }
    return result;
  }, [options, selectedTags, locale]);

  const handleSearch = (keywords?: string) => {
    setKeywords(keywords || '');
  };

  const handleChange = useCallback(
    (value?: string[]) => {
      setUpdateValue(true);
      const payload = value;
      setSelectedTags(value);
      onConfirm instanceof Function && onConfirm(payload, filterLogic);
    },
    [onConfirm, filterLogic],
  );

  const handleClose = useCallback(() => {
    if (!updateValue) {
      return;
    }
    // 选择动作已在 onChange/onLogicChange 阶段同步，关闭下拉不再重复触发 onConfirm
    setUpdateValue(false);
  }, [updateValue]);

  const handleReset = useCallback(() => {
    setKeywords('');
    setSelectedTags([]);

    const filterLogicNew = defaultValue?.logic ?? filterLogic;
    onConfirm instanceof Function && onConfirm([], filterLogicNew);
  }, [onConfirm, filterLogic]);

  // Handle filter logic change
  const handleLogicChange = useCallback((newLogic: 'AND' | 'OR') => {
    setFilterLogic(newLogic);
    // 由外层统一 onChange 处理，避免 onLogicChange 与 onConfirm 双通道重复触发
    onLogicChange instanceof Function && onLogicChange(newLogic);
  }, [onLogicChange]);

  // 全选反选
  const onSelectAll = () => {
    const values = currentTagOptions?.map((item: any) => item?.value);
    if (!selectedTags?.length) {
      handleChange(values);
    } else {
      handleChange([]);
    }
  };

  // 传出 ref 方法
  useImperativeHandle(ref, () => ({
    setSelectedTags,
    reset: handleReset,
  }));

  return (
    <SelectMultiple
      size="m"
      className="select-item"
      appearance="button"
      overlayClassName="dbi-steam-tags-selector-overlay"
      searchable
      // @ts-ignore
      filter={tagOptionsFilterAcceptor}
      boxSizeSync
      listHeight={254}
      staging={false}
      clearable={false} // isClearable} 统一设计，不展示X按钮（如打开需要解决BUG:点击清除时抛出去的值要更新）
      searchValue={keywords}
      value={selectedTags || []}
      options={currentTagOptions || []}
      onClose={handleClose}
      onChange={handleChange}
      onSearch={handleSearch}
      style={{ ...style }}
      searchPlaceholder={formatMessage({ id: searchPlaceholder }) as string}
      button={
        <div className="dbi-select-button">
          <div className="select-button-text">{formatMessage({ id: label })}</div>
          {Boolean(selectedTags?.length) ? (
            <div className="select-button-desc">{buttonLabel}</div>
          ) : (
            <div className="dbi-tag-selects-placeholder">{formatMessage({ id: placeholder })}</div>
          )}
        </div>
      }
      tips={
        <>
          {keywords && !currentTagOptions?.length ? (
            <div className="dbi-steam-tag-select-no-data">{formatMessage({ id: 'dbi_no_data' })}</div>
          ) : (
            <>
              {showLogicSelector && (
                <div className="dbi-steam-tag-logic-selector">
                  <div className="dbi-steam-tag-logic-label">
                    <span>{formatMessage({ id: 'dbi_steam_tags_logic_label' })}</span>
                    <TipContext
                      AWidth={AWidth || 300}
                      tipText={formatMessage({ id: 'dbi_steam_tags_logic_tip' })}
                    />
                  </div>
                  <Radio.Group
                    className="dbi-steam-tag-logic-radio-group"
                    value={filterLogic}
                    onChange={(value) => handleLogicChange(value as 'AND' | 'OR')}
                  >
                    <Radio name="AND">And</Radio>
                    <Radio name="OR">Or</Radio>
                  </Radio.Group>
                </div>
              )}
              <div className={`dbi-tag-selects-flex ${(currentTagOptions?.length || 0) > 4 ? '' : 'no-scrollbar'}`}>
                <span className="dbi-tag-selects-flex-lf">
                  {formatMessage({ id: tipLabel })}
                  <TipContext AWidth={AWidth || 300} tipText={formatMessage({ id: tipText }, tipTextParams)} />
                </span>
                <span className="dbi-tag-selects-flex-rt" onClick={onSelectAll}>
                  {!selectedTags?.length
                    ? formatMessage({ id: 'dbi_select_all' })
                    : formatMessage({ id: 'dbi_clear_all' })}
                </span>
              </div>
            </>
          )}
        </>
      }
    />
  );
};

export default forwardRef(SteamTagsSelector);
